import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { IconRenderer, Box, Text, useResponsiveValue, Columns, Column, Inline, ButtonIcon, IconChevron, Stack } from "braid-design-system";
import { useState, useRef } from "react";
import { useJobCardContext, ClickThroughOverlay, SaveJob, JobClassifications } from "../../JobCard.mjs";
import { DotSeparator } from "../DotSeparator/DotSeparator.mjs";
import { translations } from "../../../components/JobCardFooter/.vocab/index.mjs";
import { footerExpandBase, footerShow } from "./JobCardFooter.css.mjs";
const useViewStatus = ({
  isPremium,
  tags
}) => {
  if (isPremium || !(tags && tags.length > 0)) return;
  const mappedTags = /* @__PURE__ */ new Map();
  tags.map((t) => {
    mappedTags.set(t.type, t.label);
  });
  return mappedTags.get("APP_FINISHED") || mappedTags.get("APP_STARTED") || mappedTags.get("VIEWED");
};
const ClockIcon = () => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(IconRenderer, { children: ({ className }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "12",
    viewBox: "0 0 12 12",
    fill: "currentColor",
    className,
    "aria-hidden": true,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M1 6C1 3.22727 3.22727 1 6 1C8.77273 1 11 3.22727 11 6C11 8.77273 8.77273 11 6 11C3.22727 11 1 8.77273 1 6ZM6.45455 5.72727L8 6.5C8.22727 6.63636 8.31818 6.90909 8.22727 7.09091C8.13636 7.27273 8 7.36364 7.81818 7.36364C7.72727 7.36364 7.68182 7.36364 7.63636 7.31818L5.81818 6.40909C5.63636 6.31818 5.54545 6.18182 5.54545 6V3.27273C5.54545 3 5.72727 2.81818 6 2.81818C6.27273 2.81818 6.45455 3 6.45455 3.27273V5.72727Z"
      }
    )
  }
) }) });
const HourglassIcon = () => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(IconRenderer, { children: ({ className }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "12",
    viewBox: "0 0 12 12",
    fill: "currentColor",
    className,
    "aria-hidden": true,
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M2.5 1C2.22386 1 2 1.22386 2 1.5C2 1.77614 2.22386 2 2.5 2H3V3.5C3 4.54349 3.53275 5.46254 4.3411 6C3.53275 6.53746 3 7.45651 3 8.5V10H2.5C2.22386 10 2 10.2239 2 10.5C2 10.7761 2.22386 11 2.5 11H9.5C9.77614 11 10 10.7761 10 10.5C10 10.2239 9.77614 10 9.5 10H9V8.5C9 7.45651 8.46725 6.53746 7.6589 6C8.46725 5.46254 9 4.54349 9 3.5V2H9.5C9.77614 2 10 1.77614 10 1.5C10 1.22386 9.77614 1 9.5 1H2.5ZM6 5.5C7.10457 5.5 8 4.60457 8 3.5V2H4V3.5C4 4.60457 4.89543 5.5 6 5.5Z"
      }
    )
  }
) }) });
const useJobState = (tags) => {
  if (!tags) return;
  const jobStateTags = /* @__PURE__ */ new Map();
  tags.map((tag) => jobStateTags.set(tag.type, tag));
  return jobStateTags.get("EXPIRES_SOON") || jobStateTags.get("EARLY_APPLICANT");
};
const JobState = ({ jobStateTag }) => {
  switch (jobStateTag == null ? void 0 : jobStateTag.type) {
    case "EARLY_APPLICANT":
      return /* @__PURE__ */ jsx(EarlyApplicant, { label: jobStateTag.label });
    case "EXPIRES_SOON":
      return /* @__PURE__ */ jsx(ExpiresSoon, { label: jobStateTag.label });
    case void 0:
      return null;
  }
};
const EarlyApplicant = ({ label }) => /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { tone: "info", size: "small", weight: "strong", icon: /* @__PURE__ */ jsx(ClockIcon, {}), children: label }) });
const ExpiresSoon = ({ label }) => /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { tone: "critical", size: "small", weight: "strong", icon: /* @__PURE__ */ jsx(HourglassIcon, {}), children: label }) });
const jobListingDateAutomation = "jobListingDate";
const Classifications = ({
  isClassificationVisible
}) => {
  var _a;
  const ref = useRef(null);
  const { classificationLinks: link } = useJobCardContext();
  if (link.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      ref,
      data: { testid: "job-classification" },
      className: [
        footerExpandBase,
        { [footerShow]: isClassificationVisible }
      ],
      style: {
        maxHeight: isClassificationVisible ? (_a = ref.current) == null ? void 0 : _a.scrollHeight : 0,
        overflow: "hidden"
      },
      children: /* @__PURE__ */ jsx(Box, { marginBottom: "medium", children: /* @__PURE__ */ jsx(JobClassifications, {}) })
    }
  );
};
const JobCardFooter = () => {
  const {
    featureFlags,
    isPremium,
    listingDate,
    tags,
    id,
    saveJob: { isApplied },
    onToggleContent
  } = useJobCardContext();
  const { t } = useTranslations(translations);
  const viewStatus = useViewStatus({ isPremium, tags });
  const jobState = useJobState(tags);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  const appFinishedTag = (tags == null ? void 0 : tags.some((tag) => tag.type === "APP_FINISHED")) || isApplied;
  const [isClassificationVisible, setClassificationVisibility] = useState(false);
  if (featureFlags == null ? void 0 : featureFlags.newJobCardDensity) {
    const getFirstColumnContent = () => {
      if (isPremium) {
        return /* @__PURE__ */ jsx(Text, { size: isMobile ? "xsmall" : "small", tone: "secondary", children: t("Featured") });
      }
      if (listingDate) {
        return /* @__PURE__ */ jsx(
          Text,
          {
            size: isMobile ? "xsmall" : "small",
            tone: "secondary",
            data: { automation: jobListingDateAutomation },
            children: (featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled) && viewStatus ? /* @__PURE__ */ jsx(DotSeparator, { content: [listingDate, viewStatus] }) : listingDate
          }
        );
      }
      return null;
    };
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Classifications, { isClassificationVisible }),
      /* @__PURE__ */ jsxs(Columns, { space: "none", alignY: "center", children: [
        /* @__PURE__ */ jsx(Column, { children: getFirstColumnContent() }),
        /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(Inline, { space: "large", align: "right", alignY: "center", children: [
          /* @__PURE__ */ jsx(
            ButtonIcon,
            {
              icon: /* @__PURE__ */ jsx(
                IconChevron,
                {
                  direction: isClassificationVisible ? "up" : "down"
                }
              ),
              label: isClassificationVisible ? t("Less") : t("More"),
              variant: "transparent",
              id: `job-card-footer-expand-collapse-${id}`,
              onClick: (e) => {
                e.stopPropagation();
                onToggleContent == null ? void 0 : onToggleContent(
                  isClassificationVisible ? "collapse" : "expand"
                );
                setClassificationVisibility(!isClassificationVisible);
              }
            }
          ),
          /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(SaveJob, {}) })
        ] }) })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    !isPremium && listingDate ? /* @__PURE__ */ jsx(
      Text,
      {
        size: "small",
        tone: "secondary",
        data: { automation: jobListingDateAutomation },
        children: (featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled) && viewStatus ? /* @__PURE__ */ jsx(DotSeparator, { content: [listingDate, viewStatus] }) : listingDate
      }
    ) : null,
    !appFinishedTag && jobState && /* @__PURE__ */ jsx(JobState, { jobStateTag: jobState })
  ] });
};
export {
  JobCardFooter
};
